import React, { useState, useEffect, useRef } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Stack, MenuItem, FormControl, InputLabel, Select,
  Autocomplete, Box, Typography, CircularProgress, ToggleButton,
  ToggleButtonGroup, Chip, Switch, FormControlLabel, Collapse, Divider,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import DownloadIcon from "@mui/icons-material/Download";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { GOLD, NAVY, BORDER, TEXT_SECONDARY, ERROR, WARNING } from "../../theme/tokens";
import {
  useGenerateAiImageMutation,
  useImproveImagePromptMutation,
  usePreviewImagePromptMutation,
  useAddPlaylistItemMutation,
  useGetCompanyLogoQuery,
  useUploadCompanyLogoMutation,
  useGetMediaQuery,
  useGetTemplateZonesQuery,
} from "../../Api/attoDeskApi";
import { useNotifier } from "../../Core/Notifier";
import { extractBackendError } from "../../Core/extractBackendError";
import { FullPageLoadingOverlay } from "../Loader";

type LogoPosition =
  | "template" | "ai" | "top-left" | "top-center" | "top-right"
  | "bottom-left" | "bottom-center" | "bottom-right";

const LOGO_POSITIONS: { value: LogoPosition; label: string }[] = [
  { value: "ai", label: "AI decides" },
  { value: "top-left", label: "Top left" },
  { value: "top-center", label: "Top centre" },
  { value: "top-right", label: "Top right" },
  { value: "bottom-left", label: "Bottom left" },
  { value: "bottom-center", label: "Bottom centre" },
  { value: "bottom-right", label: "Bottom right" },
];

const LOGO_SIZES = [
  { value: "small", label: "Small" },
  { value: "medium", label: "Medium" },
  { value: "large", label: "Large" },
] as const;

const LOGO_BACKGROUNDS = [
  { value: "none", label: "None" },
  { value: "white", label: "White badge" },
  { value: "dark", label: "Dark badge" },
  { value: "panel", label: "Subtle transparent panel" },
] as const;

interface ICompanyOption {
  id: number;
  companyName: string;
}

type Aspect = "landscape" | "portrait" | "square";

const STYLES = [
  "premium", "modern", "minimal", "corporate", "restaurant", "retail",
  "festive", "elegant", "energetic", "luxury", "family-friendly",
];

// Display-only resolution per aspect (matches AiImageService::resolveSize).
const RESOLUTION: Record<Aspect, string> = {
  landscape: "1536 × 1024",
  portrait: "1024 × 1536",
  square: "1024 × 1024",
};

type GenerationPurpose = "digital_signage" | "social_media" | "print_flyer" | "general";
const GENERATION_PURPOSES: { value: GenerationPurpose; label: string }[] = [
  { value: "digital_signage", label: "Digital Signage" },
  { value: "social_media", label: "Social Media" },
  { value: "print_flyer", label: "Print Flyer" },
  { value: "general", label: "General Image" },
];
type AccuracyMode = "approximation" | "exact_compositing" | "reference_guided";
type ProductPosition =
  | "template" | "ai" | "left" | "center" | "right"
  | "top-left" | "top-center" | "top-right"
  | "bottom-left" | "bottom-center" | "bottom-right";
type ProductSize = "small" | "medium" | "large" | "hero";
type ProductBackgroundMode = "preserve" | "remove" | "transparency";
type ProductShadow = "off" | "soft" | "strong";
type ProductReflection = "none" | "subtle" | "visible";
type ProductLighting = "off" | "automatic";

const PRODUCT_POSITIONS: { value: ProductPosition; label: string }[] = [
  { value: "ai", label: "AI decides" },
  { value: "left", label: "Left" },
  { value: "center", label: "Centre" },
  { value: "right", label: "Right" },
  { value: "top-left", label: "Top left" },
  { value: "top-center", label: "Top centre" },
  { value: "top-right", label: "Top right" },
  { value: "bottom-left", label: "Bottom left" },
  { value: "bottom-center", label: "Bottom centre" },
  { value: "bottom-right", label: "Bottom right" },
];

const PRODUCT_SIZES: { value: ProductSize; label: string }[] = [
  { value: "small", label: "Small — supporting visual" },
  { value: "medium", label: "Medium — balanced" },
  { value: "large", label: "Large — dominant" },
  { value: "hero", label: "Hero — primary focal point" },
];

const ACCURACY_LABELS: Record<string, string> = {
  exact_compositing: "exact compositing",
  reference_guided: "reference-guided",
  approximation: "approximation",
};

// Colours for the layout zone-map preview, keyed by semantic role.
const ZONE_COLORS: Record<string, string> = {
  product: "#7c3aed",
  logo: "#D4AF37",
  headline: "#3b82f6",
  subheadline: "#60a5fa",
  price: "#16a34a",
  cta: "#ea580c",
  body_text: "#6B7280",
  media: "#0e7c9b",
  reserved: "#94a3b8",
  decorative: "#cbd5e1",
};

// Purpose → default product size (layout rules may override later).
const PURPOSE_PRODUCT_SIZE: Record<GenerationPurpose, ProductSize> = {
  digital_signage: "hero",
  social_media: "large",
  print_flyer: "large",
  general: "medium",
};

// Curated client-side detector for the pre-generation warning (mirrors the
// backend ProductMentionDetector — used only to prompt for a reference image).
const PRODUCT_MODEL_RE = /\b(iphone\s?\d{1,2}(\s?(pro\s?max|pro|plus|mini))?|(samsung\s)?galaxy\s?(s|note|z\s?(fold|flip))\s?\d{1,2}(\s?(ultra|plus|fe))?|google\s?pixel\s?\d{1,2}|ipad(\s?(pro|air|mini))?|macbook(\s?(pro|air))?|playstation\s?\d|ps[456]|xbox\s?(series\s?[xs]|one)|nintendo\s?switch)\b/i;

// Accurate, user-facing copy per STABLE backend error code (never localise off
// the English message). Falls back to the backend message, then a generic line.
const AI_IMAGE_ERROR_MESSAGES: Record<string, string> = {
  AI_PROVIDER_QUOTA_EXCEEDED: "Image generation is unavailable because the OpenAI account has reached its billing limit. Please update billing or contact an administrator.",
  AI_PROVIDER_NOT_CONFIGURED: "The OpenAI image provider is not configured. Please contact an administrator.",
  AI_PROVIDER_AUTH_FAILED: "The configured OpenAI API key is invalid. Please contact an administrator.",
  AI_PROVIDER_RATE_LIMITED: "Too many AI requests. Please wait a moment and try again.",
  AI_PROVIDER_TIMEOUT: "The image provider took too long to respond. Please try again.",
  AI_PROVIDER_BAD_RESPONSE: "The image provider returned an unexpected response. Please try again.",
  AI_PROVIDER_ERROR: "The image provider is temporarily unavailable. Please try again shortly.",
  // legacy alias, kept in case an older backend build is still deployed
  OPENAI_BILLING_LIMIT: "Image generation is unavailable because the OpenAI account has reached its billing limit. Please update billing or contact an administrator.",
};

const aiImageError = (err: any, fallback: string): string => {
  const code = err?.data?.code;
  if (code && AI_IMAGE_ERROR_MESSAGES[code]) return AI_IMAGE_ERROR_MESSAGES[code];
  return extractBackendError(err, fallback);
};

interface GeneratedMedia {
  id: number;
  url: string;
  name: string;
}

// Map the backend's stable AI_PROVIDER_* code to user-facing copy. Falls back
// to the generic message; never surfaces a raw provider response or stack trace.
const improvePromptErrorMessage = (err: any): string => {
  const code: string | undefined = err?.data?.code;
  switch (code) {
    case "AI_PROVIDER_RATE_LIMITED":
      return "AI is receiving too many requests. Please try again shortly.";
    case "AI_PROVIDER_QUOTA_EXCEEDED":
      return "The configured AI account has reached its usage limit. Please contact an administrator.";
    case "AI_PROVIDER_NOT_CONFIGURED":
      return "AI prompt improvement is not configured.";
    case "AI_PROVIDER_TIMEOUT":
      return "The AI service took too long to respond. Please try again.";
    default:
      // 422 validation and anything unclassified: show the field/detail if we
      // have one, else a safe generic line.
      return extractBackendError(err, "Could not improve the prompt. Please try again.");
  }
};

interface Props {
  open: boolean;
  onClose: () => void;
  isSuperAdmin: boolean;
  companies: ICompanyOption[];
  defaultCompanyId?: number | "";
  /**
   * When provided, the modal is used from a playlist and exposes a
   * "Save & Add to Playlist" action that appends the generated media to this
   * playlist using its default image duration + default transition. When
   * omitted (e.g. from Templates) those actions are hidden and the generated
   * image simply lands in the Media Library for review.
   */
  playlistId?: number;
  /** Playlist default image duration (seconds) applied to the added item. */
  imageDurationSeconds?: number;
  /** Called after the image is added to the playlist (e.g. to refetch). */
  onAddedToPlaylist?: () => void;
  /**
   * When opened for a specific layout/template, the dialog inherits that
   * layout's normalized AI zones (product/logo/text areas) so generated content
   * doesn't overlap editable layers. Zones are resolved + authorized server-side.
   */
  templateId?: number;
}

/**
 * Shared AI image generator used by both Templates and Playlists. Produces a
 * background image via the backend (OpenAI), which saves it straight to the
 * Media Library for review — nothing is published. From a playlist the
 * generated asset can additionally be added to the current playlist in one
 * step. This is the single reusable implementation; do not duplicate it.
 */
const AiImageDialog: React.FC<Props> = ({
  open, onClose, isSuperAdmin, companies, defaultCompanyId = "",
  playlistId, imageDurationSeconds = 10, onAddedToPlaylist, templateId,
}) => {
  const { showMessage, showErrorMessage } = useNotifier();
  const [generate, { isLoading }] = useGenerateAiImageMutation();
  const [improve, { isLoading: isImproving }] = useImproveImagePromptMutation();
  const [addItem, { isLoading: isAdding }] = useAddPlaylistItemMutation();

  const [companyId, setCompanyId] = useState<number | "">(defaultCompanyId);

  // The playlist's company is often not known yet when this dialog first mounts
  // (it loads asynchronously). Sync the selection to the incoming default each
  // time the dialog opens so, from a playlist, the company is pre-filled instead
  // of asking the user to pick it again.
  useEffect(() => {
    if (open) setCompanyId(defaultCompanyId);
  }, [open, defaultCompanyId]);
  const [prompt, setPrompt] = useState("");
  const [businessContext, setBusinessContext] = useState("");
  const [overlayText, setOverlayText] = useState("");
  const [style, setStyle] = useState("");
  const [aspect, setAspect] = useState<Aspect>("landscape");
  const [negative, setNegative] = useState("");
  const [result, setResult] = useState<GeneratedMedia | null>(null);
  const [resultMeta, setResultMeta] = useState<any>(null);
  const [added, setAdded] = useState(false);

  // Company-logo compositing.
  const [includeLogo, setIncludeLogo] = useState(false);
  // Render the company NAME as visible artwork. Off by default; the app normally
  // overlays exact text/logo afterward. Force-suppressed when a logo is included.
  const [bakeCompanyName, setBakeCompanyName] = useState(false);
  const [logoPosition, setLogoPosition] = useState<LogoPosition>("ai");
  const [logoSize, setLogoSize] = useState<"small" | "medium" | "large">("medium");
  const [logoBackground, setLogoBackground] =
    useState<"none" | "white" | "dark" | "panel">("none");
  const logoInputRef = useRef<HTMLInputElement | null>(null);

  // Structured promo fields (kept for editable layers; never baked into the image).
  const [showPromo, setShowPromo] = useState(false);
  const [headline, setHeadline] = useState("");
  const [regularPrice, setRegularPrice] = useState("");
  const [salePrice, setSalePrice] = useState("");
  const [promoStart, setPromoStart] = useState("");
  const [promoEnd, setPromoEnd] = useState("");
  const [cta, setCta] = useState("");
  const [disclaimer, setDisclaimer] = useState("");

  // ── Generation purpose (Digital Signage default) + product accuracy ──
  const [generationPurpose, setGenerationPurpose] = useState<GenerationPurpose>("digital_signage");
  const [productName, setProductName] = useState("");
  const [productBrand, setProductBrand] = useState("");
  const [productModel, setProductModel] = useState("");
  const [productRefMode, setProductRefMode] = useState<"none" | "library">("none");
  const [productMediaId, setProductMediaId] = useState<number | "">("");
  const [accuracyMode, setAccuracyMode] = useState<AccuracyMode>("exact_compositing");
  const [productPosition, setProductPosition] = useState<ProductPosition>("ai");
  // Strongest retail default: Hero (signage). Adjusts with generation purpose.
  const [productSize, setProductSize] = useState<ProductSize>("hero");
  const [productBackgroundMode, setProductBackgroundMode] = useState<ProductBackgroundMode>("transparency");
  const [productShadow, setProductShadow] = useState<ProductShadow>("soft");
  const [productReflection, setProductReflection] = useState<ProductReflection>("none");
  const [productLighting, setProductLighting] = useState<ProductLighting>("automatic");
  const [preserveProduct, setPreserveProduct] = useState(true);
  // Explicit acknowledgement that the user chose AI approximation for a detected
  // model without a reference. Generation is BLOCKED until they choose.
  const [approxAck, setApproxAck] = useState(false);

  // ── Template/layout zone inheritance ─────────────────────────────────
  const [useLayout, setUseLayout] = useState(true);
  const [overrideConflict, setOverrideConflict] = useState(false);
  const { data: zonesResp, error: zonesError, isFetching: zonesLoading } = useGetTemplateZonesQuery(
    {
      template_id: Number(templateId),
      aspect_ratio: aspect,
      company_id: isSuperAdmin && companyId !== "" ? Number(companyId) : undefined,
    },
    { skip: !open || !templateId || !useLayout }
  );
  const layout: any = (zonesResp as any)?.data ?? null;
  const layoutErrorCode: string | undefined = (zonesError as any)?.data?.code;
  const layoutActive = Boolean(templateId) && useLayout && Boolean(layout) && !layoutErrorCode;
  const hasProductZone = Boolean(layout?.has_product_zone);
  const hasLogoZone = Boolean(layout?.has_logo_zone);
  const layoutConflict = Array.isArray(layout?.conflicts) && layout.conflicts.length > 0;
  const templateVersion: number | undefined = layout?.template?.version;
  // A resolvable conflict blocks silent generation until the user overrides.
  const layoutBlocksGenerate = layoutActive && layoutConflict && !overrideConflict;

  // Default product/logo position to the layout when it defines those zones.
  useEffect(() => {
    if (layoutActive && hasProductZone) setProductPosition("template" as ProductPosition);
  }, [layoutActive, hasProductZone]);
  useEffect(() => {
    if (layoutActive && hasLogoZone) setLogoPosition("template" as LogoPosition);
  }, [layoutActive, hasLogoZone]);
  const [showInstructions, setShowInstructions] = useState(false);
  const [previewText, setPreviewText] = useState("");
  const [preview] = usePreviewImagePromptMutation();
  // Product-reference library (images for the selected company), lazy.
  const { data: productMediaResp } = useGetMediaQuery(
    { searchText: "", type: "image", company_id: companyId === "" ? undefined : Number(companyId) } as any,
    { skip: productRefMode !== "library" }
  );
  const productMediaList: Array<{ id: number; url?: string; original_name?: string }> =
    Array.isArray((productMediaResp as any)?.data) ? (productMediaResp as any).data : [];
  const hasProductRef = productMediaId !== "";
  const detectedModel = (prompt.match(PRODUCT_MODEL_RE)?.[0] ?? "").toLowerCase();
  const modelDetected = detectedModel !== "";
  // A specific model was requested with no reference — the user must explicitly
  // choose "Add reference" or "Continue with approximation" before generating.
  const needsApproxDecision =
    modelDetected && !hasProductRef && generationPurpose !== "general" && !approxAck;

  // Re-ask whenever the product identity changes (detected model, name, or
  // chosen reference) — a prior acknowledgement no longer applies.
  const productSignature = `${detectedModel}|${productName.trim().toLowerCase()}|${productMediaId}`;
  const prevProductSig = useRef(productSignature);
  useEffect(() => {
    if (prevProductSig.current !== productSignature) {
      prevProductSig.current = productSignature;
      setApproxAck(false);
    }
  }, [productSignature]);

  // Default product size follows the generation purpose (Hero for signage). The
  // user can still override; changing purpose resets to that purpose's default.
  useEffect(() => {
    setProductSize(PURPOSE_PRODUCT_SIZE[generationPurpose]);
  }, [generationPurpose]);

  // When a reference is added, default to Exact Product Compositing (strongest);
  // when removed, fall back to approximation.
  useEffect(() => {
    setAccuracyMode(hasProductRef ? "exact_compositing" : "approximation");
  }, [hasProductRef]);

  // Logo availability/preview — resolved server-side from the selected company.
  // Skipped until the toggle is on (and, for super admins, a company is chosen).
  const logoSkip = !includeLogo || (isSuperAdmin && companyId === "");
  const { data: logoResp, isFetching: logoLoading, refetch: refetchLogo } =
    useGetCompanyLogoQuery(
      isSuperAdmin && companyId !== "" ? { company_id: Number(companyId) } : {},
      { skip: logoSkip }
    );
  const [uploadLogo, { isLoading: isUploadingLogo }] = useUploadCompanyLogoMutation();
  const logoUrl: string | null = (logoResp as any)?.data?.logo_url ?? null;
  const hasLogo: boolean = Boolean((logoResp as any)?.data?.has_logo);

  // Single authoritative flag for the whole generate → save workflow. Drives the
  // full-page overlay and every disabled/blocked interaction. Kept separate from
  // the RTK `isLoading` so a try/finally guarantees it can never stay stuck even
  // if the mutation resolves in an unexpected shape.
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);

  const inPlaylist = typeof playlistId === "number";
  // `generating` blocks EVERYTHING (backdrop, escape, fields, buttons). `busy`
  // additionally covers the lighter improve/add operations.
  const generating = isGeneratingImage || isLoading;
  const busy = generating || isAdding || isImproving || isUploadingLogo;

  const selectedCompany: ICompanyOption | null =
    companyId === "" ? null : (companies.find((c) => c.id === companyId) ?? null);

  // Can't composite a logo the company doesn't have — block Generate until one
  // is uploaded (or the toggle is turned off).
  const logoBlocksGenerate = includeLogo && !logoLoading && !hasLogo;

  const canGenerate =
    prompt.trim().length >= 3 &&
    (!isSuperAdmin || companyId !== "") &&
    !logoBlocksGenerate &&
    !needsApproxDecision &&
    !layoutBlocksGenerate &&
    !(Boolean(templateId) && useLayout && Boolean(layoutErrorCode)) &&
    !busy;
  const canImprove = prompt.trim().length >= 3 && !busy;

  const reset = () => {
    setPrompt(""); setBusinessContext(""); setOverlayText("");
    setStyle(""); setAspect("landscape"); setNegative("");
    setResult(null); setAdded(false);
    setIncludeLogo(false); setBakeCompanyName(false); setLogoPosition("ai"); setLogoSize("medium");
    setLogoBackground("none");
    setShowPromo(false);
    setHeadline(""); setRegularPrice(""); setSalePrice("");
    setPromoStart(""); setPromoEnd(""); setCta(""); setDisclaimer("");
    // Product/instructions reset (generation purpose is intentionally kept).
    setProductName(""); setProductBrand(""); setProductModel("");
    setProductRefMode("none"); setProductMediaId(""); setAccuracyMode("exact_compositing");
    setProductPosition("ai"); setProductSize(PURPOSE_PRODUCT_SIZE[generationPurpose]); setPreserveProduct(true);
    setProductBackgroundMode("transparency"); setProductShadow("soft");
    setProductReflection("none"); setProductLighting("automatic");
    setApproxAck(false); setShowInstructions(false); setPreviewText("");
    setOverrideConflict(false);
    setResultMeta(null);
  };

  // Upload/replace the company's saved logo, then re-check availability.
  const handleLogoUpload = async (file?: File) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    if (isSuperAdmin && companyId !== "") fd.append("company_id", String(companyId));
    try {
      await uploadLogo(fd).unwrap();
      showMessage("Company logo updated.");
      refetchLogo();
    } catch (e: any) {
      showErrorMessage(extractBackendError(e, "Logo upload failed"));
    }
  };

  const handleClose = () => {
    if (busy) return; // don't abandon an in-flight paid request
    reset();
    onClose();
  };

  // Compose the final prompt from the scene idea + optional business context
  // and optional on-image text. The scene idea alone drives "Improve Prompt".
  const buildFinalPrompt = (): string => {
    let p = prompt.trim();
    if (businessContext.trim()) p += `\n\nBusiness context: ${businessContext.trim()}`;
    if (overlayText.trim()) p += `\n\nInclude this text in the image: "${overlayText.trim()}"`;
    return p;
  };

  const handleImprove = async () => {
    // Duplicate-click guard (button is also disabled while busy).
    if (isImproving) return;
    try {
      const res: any = await improve({
        prompt: prompt.trim(),
        style: style || undefined,
        aspect_ratio: aspect,
        // Structured logo state (not inferred from the prompt text) so the
        // rewrite reserves space for the official logo instead of forbidding it.
        include_logo: includeLogo || undefined,
        bake_company_name: bakeCompanyName || undefined,
        logo_position: includeLogo ? logoPosition : undefined,
        logo_size: includeLogo ? logoSize : undefined,
        logo_background: includeLogo ? logoBackground : undefined,
        // Structured product state so the rewrite steers per accuracy mode.
        product_accuracy_mode: hasProductRef
          ? accuracyMode
          : (modelDetected ? "approximation" : undefined),
        product_name: productName.trim() || undefined,
        product_brand: productBrand.trim() || undefined,
        product_model: productModel.trim() || undefined,
      }).unwrap();
      const improved = res?.data?.improved;
      if (improved) {
        // Replace the editable prompt — the user can still tweak it before
        // generating. The dialog stays open regardless of outcome.
        setPrompt(improved);
        showMessage("Prompt improved — review it, then Generate.");
      } else {
        showErrorMessage("Could not improve the prompt. Please try again.");
      }
    } catch (e: any) {
      // Branch on the STABLE backend code, not the (localisable) message. On any
      // failure the original prompt is untouched and the dialog remains open, so
      // the user can edit manually or still Generate.
      showErrorMessage(improvePromptErrorMessage(e));
    }
  };

  const handleGenerate = async () => {
    // Hard duplicate-request guard: even if a disabled button is somehow
    // activated (keyboard, race), never fire a second paid request.
    if (generating) return;
    setIsGeneratingImage(true);
    try {
      const res: any = await generate({
        prompt: buildFinalPrompt(),
        aspect_ratio: aspect,
        style: style || undefined,
        negative: negative.trim() || undefined,
        company_id: isSuperAdmin ? Number(companyId) : undefined,
        // Generation purpose + product accuracy.
        generation_purpose: generationPurpose,
        product_name: productName.trim() || undefined,
        product_brand: productBrand.trim() || undefined,
        product_model: productModel.trim() || undefined,
        product_reference_media_id: hasProductRef ? Number(productMediaId) : undefined,
        product_accuracy_mode: hasProductRef ? accuracyMode : (modelDetected && approxAck ? "approximation" : undefined),
        preserve_product: hasProductRef ? preserveProduct : undefined,
        // Placement + visual-integration only apply to exact compositing.
        product_position: hasProductRef && accuracyMode === "exact_compositing" ? productPosition : undefined,
        product_size: hasProductRef && accuracyMode === "exact_compositing" ? productSize : undefined,
        product_background_mode: hasProductRef && accuracyMode === "exact_compositing" ? productBackgroundMode : undefined,
        product_shadow: hasProductRef && accuracyMode === "exact_compositing" ? productShadow : undefined,
        product_reflection: hasProductRef && accuracyMode === "exact_compositing" ? productReflection : undefined,
        product_lighting_integration: hasProductRef && accuracyMode === "exact_compositing" ? productLighting : undefined,
        // Logo compositing (server resolves the actual logo file).
        include_logo: includeLogo || undefined,
        bake_company_name: bakeCompanyName || undefined,
        logo_position: includeLogo ? logoPosition : undefined,
        logo_size: includeLogo ? logoSize : undefined,
        logo_background: includeLogo ? logoBackground : undefined,
        // Template/layout zone inheritance. Only the id + flags are sent — the
        // zone coordinates are resolved + authorized SERVER-SIDE (never trusted
        // from the browser). template_version guards against a stale layout.
        template_id: layoutActive ? Number(templateId) : undefined,
        use_template_zones: layoutActive || undefined,
        template_version: layoutActive ? templateVersion : undefined,
        override_template_conflict: layoutActive && layoutConflict ? overrideConflict : undefined,
        // Structured promo fields (editable layers later; not baked in).
        headline: headline.trim() || undefined,
        regular_price: regularPrice.trim() || undefined,
        sale_price: salePrice.trim() || undefined,
        promo_start: promoStart || undefined,
        promo_end: promoEnd || undefined,
        cta: cta.trim() || undefined,
        disclaimer: disclaimer.trim() || undefined,
      }).unwrap();
      // The backend generates AND persists to the Media Library atomically, so a
      // returned media object means the save genuinely succeeded — only then do
      // we show the preview + "Saved to Media Library".
      const media = res?.data?.media;
      if (media?.id && media?.url) {
        setResult({ id: media.id, url: media.url, name: media.original_name });
        const od = res?.data?.output_data ?? {};
        setResultMeta({
          purpose: res?.data?.generation_purpose,
          accuracyMode: od.accuracy_mode ?? res?.data?.product_accuracy_mode,
          position: od.product_position,
          size: od.product_size,
          referenceUsed: Boolean(res?.data?.product_reference_media_id) || Boolean(od.product_composited),
          isApproximation: Boolean(od.is_approximation),
          productComposited: Boolean(od.product_composited),
          logoComposited: Boolean(od.logo_composited),
          useTemplateZones: Boolean(od.use_template_zones),
          templateName: od.template_name,
          productZoneInherited: Boolean(od.product_zone_inherited),
          logoZoneInherited: Boolean(od.logo_zone_inherited),
        });
        setAdded(false);
        showMessage("Image generated and saved to the Media Library.");
      } else {
        // Generation reported success but nothing was persisted — surface it as
        // distinct from an AI failure, and never claim it was saved.
        setResult(null);
        showErrorMessage(
          "The image was generated, but could not be saved to the Media Library. Please try again."
        );
      }
    } catch (e: any) {
      // On any failure the modal stays open and every entered field is preserved
      // (we don't reset), so the user can retry without re-typing.
      showErrorMessage(aiImageError(e, "Could not generate image"));
    } finally {
      // finally => the overlay can never remain stuck, on success OR error.
      setIsGeneratingImage(false);
    }
  };

  const handleDownload = async () => {
    if (!result) return;
    try {
      const resp = await fetch(result.url);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = result.name || "ai-image.png";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      window.open(result.url, "_blank"); // fallback if fetch is blocked
    }
  };

  const handleAddToPlaylist = async () => {
    if (!result || !inPlaylist) return;
    try {
      // Backend defaults use_playlist_default_transition = true, so the new
      // item inherits the playlist's default transition + transition duration.
      await addItem({
        id: playlistId as number,
        media_id: result.id,
        duration: imageDurationSeconds,
      }).unwrap();
      setAdded(true);
      showMessage("Image added to the playlist.");
      onAddedToPlaylist?.();
      handleClose();
    } catch (e: any) {
      // Requirement 11: generation already stored the image in the library,
      // so it is never lost — surface that clearly.
      showErrorMessage(
        extractBackendError(
          e,
          "The image was generated and saved to the Media Library, but could not be added to the playlist. You can add it from the Media Library."
        )
      );
    }
  };

  return (
    <>
    <Dialog
      open={open}
      // While generating, block BOTH dismiss paths: backdrop click is ignored by
      // handleClose's busy guard, and Escape is disabled outright.
      onClose={(_e, reason) => {
        if (busy && (reason === "backdropClick" || reason === "escapeKeyDown")) return;
        handleClose();
      }}
      disableEscapeKeyDown={busy}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <AutoAwesomeIcon sx={{ color: GOLD }} />
        <span>Generate Image with AI</span>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2.25} sx={{ mt: 1 }}>
          {isSuperAdmin && (
            <Autocomplete
              fullWidth
              disabled={busy}
              options={companies}
              value={selectedCompany}
              onChange={(_, v) => setCompanyId(v ? v.id : "")}
              getOptionLabel={(o) => o.companyName}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderInput={(params) => (
                <TextField {...params} label="Company *"
                  placeholder="— Select a company —"
                  helperText="Brand colours and context are pulled from this company." />
              )}
            />
          )}

          {/* Layout/template zone inheritance — the selected layout controls
              where the product, logo and text belong so the AI scene never
              overlaps editable layers. Zones are resolved server-side. */}
          {templateId && (
            <Box sx={{ border: `1px solid ${layoutErrorCode ? ERROR : BORDER}`, borderRadius: 1.5, p: 1.5 }}>
              <FormControlLabel
                sx={{ m: 0 }}
                control={<Switch checked={useLayout} disabled={busy} onChange={(e) => setUseLayout(e.target.checked)} />}
                label={<Typography sx={{ fontSize: 14, fontWeight: 600, color: NAVY }}>Use the selected layout's placement</Typography>}
              />

              {useLayout && zonesLoading && (
                <Typography sx={{ fontSize: 12, color: TEXT_SECONDARY, mt: 0.5 }}>Loading layout…</Typography>
              )}

              {useLayout && layoutErrorCode && (
                <Typography data-testid="layout-error" sx={{ fontSize: 12.5, color: ERROR, mt: 0.5, fontWeight: 600 }}>
                  {layoutErrorCode === "AI_TEMPLATE_ORIENTATION_MISMATCH"
                    ? "This layout's orientation doesn't match the selected output. Choose a matching orientation or layout."
                    : layoutErrorCode === "AI_TEMPLATE_CONTEXT_STALE"
                      ? "This layout changed since you opened it. Please reopen or refresh the layout."
                      : "The selected layout could not be used."}
                </Typography>
              )}

              {layoutActive && (
                <Box sx={{ mt: 1 }}>
                  <Typography sx={{ fontSize: 12, color: NAVY, fontWeight: 600, mb: 0.75 }}>
                    Placement inherited from the selected layout.
                  </Typography>

                  {/* Compact visual zone map (normalized boxes on the canvas). */}
                  <Box sx={{ position: "relative", width: "100%", aspectRatio: aspect === "portrait" ? "9 / 16" : aspect === "square" ? "1 / 1" : "16 / 9", bgcolor: "rgba(15,23,42,0.04)", borderRadius: 1, border: `1px solid ${BORDER}`, overflow: "hidden", maxWidth: aspect === "portrait" ? 140 : 260 }}>
                    {(layout?.zones ?? []).map((z: any) => (
                      <Box key={z.id} data-testid={`zone-${z.type}`}
                        sx={{
                          position: "absolute",
                          left: `${z.x * 100}%`, top: `${z.y * 100}%`,
                          width: `${z.width * 100}%`, height: `${z.height * 100}%`,
                          border: `1px solid ${ZONE_COLORS[z.type] ?? TEXT_SECONDARY}`,
                          bgcolor: `${ZONE_COLORS[z.type] ?? TEXT_SECONDARY}22`,
                          display: "flex", alignItems: "center", justifyContent: "center",
                        }}>
                        <Typography sx={{ fontSize: 8, color: ZONE_COLORS[z.type] ?? TEXT_SECONDARY, fontWeight: 700, textAlign: "center", lineHeight: 1 }}>
                          {z.type.replace("_", " ")}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  {layoutConflict && (
                    <Box sx={{ mt: 1 }}>
                      <Typography sx={{ fontSize: 12, color: ERROR, fontWeight: 600 }}>
                        This layout has overlapping content zones. Resolve it in the layout, or override to continue.
                      </Typography>
                      <FormControlLabel
                        control={<Switch size="small" checked={overrideConflict} disabled={busy} onChange={(e) => setOverrideConflict(e.target.checked)} />}
                        label={<Typography sx={{ fontSize: 12 }}>Override and generate anyway</Typography>}
                      />
                    </Box>
                  )}

                  {!hasProductZone && (
                    <Typography sx={{ fontSize: 11.5, color: TEXT_SECONDARY, mt: 0.5 }}>
                      No product zone defined in this layout — using the selected product position.
                    </Typography>
                  )}
                  {!hasLogoZone && includeLogo && (
                    <Typography sx={{ fontSize: 11.5, color: TEXT_SECONDARY }}>
                      No logo zone defined in this layout — using the selected logo position.
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          )}

          {/* Include Company Logo — the exact saved logo is composited onto the
              generated flyer server-side (never redrawn by the AI). */}
          <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 1.5, p: 1.5 }}>
            <FormControlLabel
              sx={{ m: 0 }}
              control={
                <Switch
                  checked={includeLogo}
                  onChange={(e) => {
                    setIncludeLogo(e.target.checked);
                    // Prevent duplicate branding: the official logo is overlaid,
                    // so never also bake the company name into the artwork.
                    if (e.target.checked) setBakeCompanyName(false);
                  }}
                  disabled={busy || (isSuperAdmin && companyId === "")}
                />
              }
              label={
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: NAVY }}>
                  Include Company Logo
                </Typography>
              }
            />
            {isSuperAdmin && companyId === "" && (
              <Typography sx={{ fontSize: 12, color: TEXT_SECONDARY, ml: 0.5 }}>
                Select a company first to use its logo.
              </Typography>
            )}

            {/* Bake company name into image — off by default. The default signage
                workflow keeps the name OUT of the artwork (overlaid as editable
                text/logo afterward). Disabled while a logo is included, to
                prevent duplicate branding. */}
            <FormControlLabel
              sx={{ m: 0, mt: 0.5 }}
              control={
                <Switch
                  size="small"
                  checked={bakeCompanyName && !includeLogo}
                  disabled={busy || includeLogo}
                  onChange={(e) => setBakeCompanyName(e.target.checked)}
                />
              }
              label={
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: NAVY }}>
                  Bake company name into image
                </Typography>
              }
            />
            {includeLogo ? (
              <Typography sx={{ fontSize: 11.5, color: TEXT_SECONDARY, ml: 0.5 }}>
                Disabled to avoid duplicate branding — the official logo is overlaid instead, so the AI won't render the company name.
              </Typography>
            ) : bakeCompanyName ? (
              <Typography sx={{ fontSize: 11.5, color: WARNING, ml: 0.5, fontWeight: 600 }}>
                Heads up: AI-rendered brand text may be inaccurate. Prefer adding the name as an editable layer.
              </Typography>
            ) : (
              <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY, ml: 0.5 }}>
                Off: the company name is context only and won't appear in the image. The app adds exact text/logo afterward.
              </Typography>
            )}

            {/* Hidden picker reused by both the "Upload" and "Change" actions. */}
            <input
              ref={logoInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              hidden
              data-testid="logo-file-input"
              onChange={(e) => { handleLogoUpload(e.target.files?.[0]); e.target.value = ""; }}
            />

            <Collapse in={includeLogo} unmountOnExit>
              <Stack spacing={1.75} sx={{ mt: 1.5 }}>
                {logoLoading ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: TEXT_SECONDARY }}>
                    <CircularProgress size={16} sx={{ color: GOLD }} />
                    <Typography sx={{ fontSize: 13 }}>Loading company logo…</Typography>
                  </Box>
                ) : hasLogo && logoUrl ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box sx={{ width: 56, height: 56, borderRadius: 1, border: `1px solid ${BORDER}`, bgcolor: "#f8fafc", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", flexShrink: 0 }}>
                      <Box component="img" src={logoUrl} alt="Company logo"
                        sx={{ maxWidth: "100%", maxHeight: "100%", display: "block" }} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: 12.5, color: NAVY, fontWeight: 600 }}>
                        Company logo ready
                      </Typography>
                      <Button size="small" onClick={() => logoInputRef.current?.click()}
                        disabled={busy} startIcon={<UploadFileIcon sx={{ fontSize: 15 }} />}
                        sx={{ textTransform: "none", color: NAVY, px: 0.5, minWidth: 0 }}>
                        {isUploadingLogo ? "Uploading…" : "Change logo"}
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Box>
                    <Typography sx={{ fontSize: 13, color: ERROR, mb: 1 }}>
                      No company logo is available. Upload a logo before generating.
                    </Typography>
                    <Button size="small" variant="outlined"
                      startIcon={isUploadingLogo ? <CircularProgress size={14} /> : <UploadFileIcon sx={{ fontSize: 16 }} />}
                      onClick={() => logoInputRef.current?.click()} disabled={busy}
                      sx={{ textTransform: "none" }}>
                      {isUploadingLogo ? "Uploading…" : "Upload Logo"}
                    </Button>
                  </Box>
                )}

                <FormControl fullWidth size="small" disabled={busy}>
                  <InputLabel>Logo position</InputLabel>
                  <Select label="Logo position" value={logoPosition}
                    SelectDisplayProps={{ "data-testid": "logo-position" } as any}
                    onChange={(e) => setLogoPosition(e.target.value as LogoPosition)}>
                    {layoutActive && hasLogoZone && (
                      <MenuItem value="template">From selected layout</MenuItem>
                    )}
                    {LOGO_POSITIONS.map((o) => (
                      <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {layoutActive && hasLogoZone && logoPosition !== "template" && (
                  <Typography sx={{ fontSize: 11, color: WARNING, fontWeight: 600 }}>
                    Custom placement overrides the selected layout's logo zone.
                  </Typography>
                )}

                <Box sx={{ display: "flex", gap: 1.5 }}>
                  <FormControl fullWidth size="small" disabled={busy}>
                    <InputLabel>Logo size</InputLabel>
                    <Select label="Logo size" value={logoSize}
                      SelectDisplayProps={{ "data-testid": "logo-size" } as any}
                      onChange={(e) => setLogoSize(e.target.value as "small" | "medium" | "large")}>
                      {LOGO_SIZES.map((o) => (
                        <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth size="small" disabled={busy}>
                    <InputLabel>Logo background</InputLabel>
                    <Select label="Logo background" value={logoBackground}
                      SelectDisplayProps={{ "data-testid": "logo-background" } as any}
                      onChange={(e) => setLogoBackground(e.target.value as "none" | "white" | "dark" | "panel")}>
                      {LOGO_BACKGROUNDS.map((o) => (
                        <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                {/* Make the two-step branding explicit: the AI never draws the
                    logo — the app overlays the exact uploaded file afterward. */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <CheckCircleIcon sx={{ fontSize: 15, color: GOLD }} />
                  <Typography sx={{ fontSize: 12, color: NAVY, fontWeight: 600 }}>
                    Official company logo will be added afterward.
                  </Typography>
                </Box>
              </Stack>
            </Collapse>
          </Box>

          <FormControl fullWidth disabled={busy}>
            <InputLabel>Generation purpose *</InputLabel>
            <Select
              label="Generation purpose *"
              value={generationPurpose}
              onChange={(e) => setGenerationPurpose(e.target.value as GenerationPurpose)}
            >
              {GENERATION_PURPOSES.map((p) => (
                <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {generationPurpose === "digital_signage" && (
            <Typography sx={{ fontSize: 11.5, color: TEXT_SECONDARY, mt: -1 }}>
              Optimized for digital-signage screens. Text, prices and logos can be added as editable layers after generation.
            </Typography>
          )}

          <TextField
            label="Prompt *"
            placeholder="Premium Canada Day biryani promotion, warm restaurant lighting, space on the left for an offer…"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            disabled={busy}
            // Cap the auto-resize: a long improved prompt otherwise grows the
            // textarea unbounded, and TextareaAutosize's re-measure loop triggers
            // the benign "ResizeObserver loop" warning. maxRows adds internal
            // scrolling instead of endless height recalculation.
            multiline minRows={3} maxRows={10} fullWidth
            helperText="Describe the background scene. Headlines, prices and logos are best added as editable layers."
          />

          {/* Prominent, BLOCKING product-reference prompt directly under the
              scene prompt. Generate is disabled until the user explicitly picks
              one of the two actions (needsApproxDecision → canGenerate false). */}
          {needsApproxDecision && (
            <Box sx={{ p: 1.75, borderRadius: 1.5, border: `2px solid ${ERROR}`, bgcolor: "rgba(220,38,38,0.06)" }}>
              <Typography sx={{ fontSize: 14, fontWeight: 800, color: ERROR }}>
                Exact product reference recommended
              </Typography>
              <Typography sx={{ fontSize: 12, color: TEXT_SECONDARY, mt: 0.5 }}>
                You requested a specific product model. AI may generate an older, generic or
                inaccurate version unless you provide the real product image.
              </Typography>
              <Box sx={{ mt: 1.25, display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Button size="small" variant="contained" color="secondary"
                  onClick={() => { setProductRefMode("library"); }}
                  sx={{ textTransform: "none", fontWeight: 700 }}>
                  Add Product Reference
                </Button>
                <Button size="small" variant="outlined"
                  onClick={() => { setApproxAck(true); setAccuracyMode("approximation"); }}
                  sx={{ textTransform: "none", fontWeight: 600 }}>
                  Continue with AI Approximation
                </Button>
              </Box>
            </Box>
          )}

          {/* Persistent badge once the user has knowingly chosen approximation. */}
          {modelDetected && !hasProductRef && approxAck && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, px: 1, py: 0.5, borderRadius: 1, bgcolor: "rgba(234,88,12,0.10)" }}>
              <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: WARNING }}>
                AI approximation — product details may not be exact.
              </Typography>
              <Button size="small" onClick={() => setProductRefMode("library")}
                sx={{ textTransform: "none", fontSize: 11, minWidth: 0, px: 0.5, color: NAVY }}>
                Add reference
              </Button>
            </Box>
          )}

          <Box sx={{ mt: -1 }}>
            <Button
              size="small"
              startIcon={isImproving ? <CircularProgress size={14} sx={{ color: GOLD }} /> : <AutoAwesomeIcon sx={{ fontSize: 16 }} />}
              onClick={handleImprove}
              disabled={!canImprove}
              sx={{ textTransform: "none", color: NAVY, fontWeight: 600 }}
            >
              Improve Prompt with AI
            </Button>
          </Box>

          <TextField
            label="Business / company context (optional)"
            placeholder="e.g. Upscale Indian restaurant, Canada Day weekend special"
            value={businessContext}
            onChange={(e) => setBusinessContext(e.target.value)}
            disabled={busy}
            fullWidth
          />

          <Box>
            <Typography sx={{ fontSize: 12, color: TEXT_SECONDARY, mb: 0.75 }}>
              Orientation
            </Typography>
            <ToggleButtonGroup
              exclusive size="small" value={aspect}
              disabled={busy}
              onChange={(_, v) => v && setAspect(v)}
            >
              <ToggleButton value="landscape">16:9 Landscape</ToggleButton>
              <ToggleButton value="portrait">9:16 Portrait</ToggleButton>
              <ToggleButton value="square">1:1 Square</ToggleButton>
            </ToggleButtonGroup>
            <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY, mt: 0.5 }}>
              Resolution: {RESOLUTION[aspect]} px
            </Typography>
          </Box>

          {/* ── Product reference & accuracy ── */}
          <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 1, p: 1.5 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: NAVY, mb: 1 }}>
              Product reference (optional)
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
              <TextField size="small" label="Brand" value={productBrand} disabled={busy}
                onChange={(e) => setProductBrand(e.target.value)} fullWidth />
              <TextField size="small" label="Exact model" value={productModel} disabled={busy}
                onChange={(e) => setProductModel(e.target.value)} fullWidth />
            </Stack>
            <TextField size="small" label="Product name (optional)" value={productName} disabled={busy}
              onChange={(e) => setProductName(e.target.value)} fullWidth sx={{ mb: 1 }} />

            <ToggleButtonGroup exclusive size="small" value={productRefMode} disabled={busy}
              onChange={(_, v) => { if (v) { setProductRefMode(v); if (v === "none") setProductMediaId(""); } }} sx={{ mb: 1, flexWrap: "wrap" }}>
              <ToggleButton value="none">No reference</ToggleButton>
              <ToggleButton value="library">Select from Media Library</ToggleButton>
            </ToggleButtonGroup>
            <Typography sx={{ fontSize: 10.5, color: TEXT_SECONDARY, mb: productRefMode === "library" ? 1 : 0 }}>
              For an exact product, use a transparent-PNG reference. Upload it to the Media Library first; product catalogue linking is coming soon.
            </Typography>

            {productRefMode === "library" && (
              <Box sx={{ display: "flex", gap: 1, overflowX: "auto", pb: 0.5 }}>
                {productMediaList.length === 0 && (
                  <Typography sx={{ fontSize: 11.5, color: TEXT_SECONDARY }}>No images in this company's library yet.</Typography>
                )}
                {productMediaList.slice(0, 24).map((m) => (
                  <Box key={m.id} onClick={() => setProductMediaId(m.id)}
                    sx={{
                      flex: "0 0 auto", width: 64, height: 64, borderRadius: 1, cursor: "pointer",
                      border: `2px solid ${productMediaId === m.id ? GOLD : BORDER}`,
                      backgroundImage: m.url ? `url(${m.url})` : undefined,
                      backgroundSize: "cover", backgroundPosition: "center",
                    }} />
                ))}
              </Box>
            )}

            {hasProductRef && (
              <Box sx={{ mt: 1.5 }}>
                <Typography sx={{ fontSize: 11.5, color: TEXT_SECONDARY, mb: 0.5 }}>Accuracy mode</Typography>
                <ToggleButtonGroup exclusive size="small" value={accuracyMode} disabled={busy}
                  onChange={(_, v) => v && setAccuracyMode(v)} sx={{ mb: 0.5, flexWrap: "wrap" }}>
                  <ToggleButton value="exact_compositing" data-testid="mode-exact">Exact Product Compositing — Recommended</ToggleButton>
                  <ToggleButton value="reference_guided" data-testid="mode-reference">Reference-Guided AI Generation</ToggleButton>
                  <ToggleButton value="approximation" data-testid="mode-approx">AI Approximation</ToggleButton>
                </ToggleButtonGroup>
                <Typography sx={{ fontSize: 10.5, color: TEXT_SECONDARY, mb: 1 }}>
                  {accuracyMode === "exact_compositing"
                    ? "Generate only the environment, then overlay the exact original product image — no AI redrawing."
                    : accuracyMode === "reference_guided"
                      ? "The reference guides the provider, but model-specific details may still change (not exact)."
                      : "No reference is used; the provider creates its own interpretation."}
                </Typography>
                <FormControlLabel
                  control={<Switch size="small" checked={preserveProduct} disabled={busy} onChange={(e) => setPreserveProduct(e.target.checked)} />}
                  label={<Typography sx={{ fontSize: 12 }}>Preserve product exactly</Typography>}
                />
                {accuracyMode === "exact_compositing" && (
                  <Stack spacing={1} sx={{ mt: 1 }}>
                    <Stack direction="row" spacing={1}>
                      <FormControl size="small" fullWidth disabled={busy}>
                        <InputLabel>Position</InputLabel>
                        <Select label="Position" value={productPosition}
                          SelectDisplayProps={{ "data-testid": "product-position" } as any}
                          onChange={(e) => setProductPosition(e.target.value as ProductPosition)}>
                          {layoutActive && hasProductZone && (
                            <MenuItem value="template">From selected layout</MenuItem>
                          )}
                          {PRODUCT_POSITIONS.map((p) => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
                        </Select>
                      </FormControl>
                      <FormControl size="small" fullWidth disabled={busy}>
                        <InputLabel>Size</InputLabel>
                        <Select label="Size" value={productSize}
                          SelectDisplayProps={{ "data-testid": "product-size" } as any}
                          onChange={(e) => setProductSize(e.target.value as ProductSize)}>
                          {PRODUCT_SIZES.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Stack>
                    {layoutActive && hasProductZone && productPosition !== "template" && (
                      <Typography data-testid="product-override-warning" sx={{ fontSize: 11, color: WARNING, fontWeight: 600 }}>
                        Custom placement overrides the selected layout's product zone.
                      </Typography>
                    )}
                    <Stack direction="row" spacing={1}>
                      <FormControl size="small" fullWidth disabled={busy}>
                        <InputLabel>Background</InputLabel>
                        <Select label="Background" value={productBackgroundMode}
                          onChange={(e) => setProductBackgroundMode(e.target.value as ProductBackgroundMode)}>
                          <MenuItem value="transparency">Use existing transparency</MenuItem>
                          <MenuItem value="preserve">Preserve original background</MenuItem>
                          <MenuItem value="remove">Remove background automatically</MenuItem>
                        </Select>
                      </FormControl>
                      <FormControl size="small" fullWidth disabled={busy}>
                        <InputLabel>Shadow</InputLabel>
                        <Select label="Shadow" value={productShadow}
                          onChange={(e) => setProductShadow(e.target.value as ProductShadow)}>
                          <MenuItem value="off">Off</MenuItem>
                          <MenuItem value="soft">Soft</MenuItem>
                          <MenuItem value="strong">Strong</MenuItem>
                        </Select>
                      </FormControl>
                    </Stack>
                    <Stack direction="row" spacing={1}>
                      <FormControl size="small" fullWidth disabled={busy}>
                        <InputLabel>Reflection</InputLabel>
                        <Select label="Reflection" value={productReflection}
                          onChange={(e) => setProductReflection(e.target.value as ProductReflection)}>
                          <MenuItem value="none">None</MenuItem>
                          <MenuItem value="subtle">Subtle</MenuItem>
                          <MenuItem value="visible">Visible</MenuItem>
                        </Select>
                      </FormControl>
                      <FormControl size="small" fullWidth disabled={busy}>
                        <InputLabel>Lighting integration</InputLabel>
                        <Select label="Lighting integration" value={productLighting}
                          onChange={(e) => setProductLighting(e.target.value as ProductLighting)}>
                          <MenuItem value="automatic">Automatic</MenuItem>
                          <MenuItem value="off">Off</MenuItem>
                        </Select>
                      </FormControl>
                    </Stack>
                    <Typography sx={{ fontSize: 10, color: TEXT_SECONDARY }}>
                      Shadow renders now. Background removal, reflection and lighting integration are
                      recorded and steer the scene; automatic background removal is not yet available —
                      upload a transparent PNG.
                    </Typography>
                  </Stack>
                )}
              </Box>
            )}
          </Box>

          {/* ── Generation instructions preview (Part 4) ── */}
          <Box>
            <Button size="small" onClick={async () => {
              const next = !showInstructions; setShowInstructions(next);
              if (next) {
                try {
                  const res: any = await preview({
                    prompt: prompt.trim(),
                    generation_purpose: generationPurpose,
                    aspect_ratio: aspect,
                    style: style || undefined,
                    negative: negative.trim() || undefined,
                    company_id: isSuperAdmin && companyId !== "" ? Number(companyId) : undefined,
                    product_accuracy_mode: hasProductRef ? accuracyMode : (modelDetected ? "approximation" : undefined),
                    product_reference_media_id: hasProductRef ? Number(productMediaId) : undefined,
                    product_name: productName.trim() || undefined,
                    product_brand: productBrand.trim() || undefined,
                    product_model: productModel.trim() || undefined,
                    product_position: hasProductRef && accuracyMode === "exact_compositing" ? productPosition : undefined,
                    product_size: hasProductRef && accuracyMode === "exact_compositing" ? productSize : undefined,
                    include_logo: includeLogo || undefined,
                    bake_company_name: bakeCompanyName || undefined,
                    logo_position: includeLogo ? logoPosition : undefined,
                    logo_size: includeLogo ? logoSize : undefined,
                    logo_background: includeLogo ? logoBackground : undefined,
                  }).unwrap();
                  setPreviewText(res?.data?.final_provider_prompt || "(empty)");
                } catch { setPreviewText("(preview unavailable)"); }
              }
            }} sx={{ textTransform: "none", color: TEXT_SECONDARY, fontWeight: 600 }}>
              {showInstructions ? "Hide" : "Show"} generation instructions
            </Button>
            <Collapse in={showInstructions}>
              <Box sx={{ mt: 0.5, p: 1.25, borderRadius: 1, bgcolor: "rgba(15,23,42,0.04)", border: `1px solid ${BORDER}` }}>
                <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY, whiteSpace: "pre-wrap" }}>
                  {previewText || "…"}
                </Typography>
              </Box>
            </Collapse>
          </Box>

          <FormControl fullWidth disabled={busy}>
            <InputLabel>Style</InputLabel>
            <Select label="Style" value={style} onChange={(e) => setStyle(e.target.value)}>
              <MenuItem value=""><em>None</em></MenuItem>
              {STYLES.map((s) => (
                <MenuItem key={s} value={s} sx={{ textTransform: "capitalize" }}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Text to display on the image (optional)"
            placeholder="e.g. Canada Day Special"
            value={overlayText}
            onChange={(e) => setOverlayText(e.target.value)}
            disabled={busy}
            fullWidth
          />

          <TextField
            label="Avoid (optional)"
            placeholder="e.g. watermarks, cluttered background"
            value={negative}
            onChange={(e) => setNegative(e.target.value)}
            disabled={busy}
            fullWidth
          />

          {/* Promotional details — captured as structured data for editable
              template layers. Deliberately NOT rendered into the AI image so
              prices, wording and spelling stay accurate. */}
          <Box>
            <Button
              size="small"
              onClick={() => setShowPromo((v) => !v)}
              disabled={busy}
              sx={{ textTransform: "none", color: NAVY, fontWeight: 600, px: 0.5 }}
            >
              {showPromo ? "Hide promotional details" : "Add promotional details (optional)"}
            </Button>
            <Collapse in={showPromo} unmountOnExit>
              <Stack spacing={1.75} sx={{ mt: 1 }}>
                <Typography sx={{ fontSize: 11.5, color: TEXT_SECONDARY }}>
                  These stay editable after generation (added as text layers), so
                  prices and wording remain exact — they are not drawn into the image.
                </Typography>
                <TextField label="Headline" size="small" fullWidth disabled={busy}
                  value={headline} onChange={(e) => setHeadline(e.target.value)}
                  placeholder="e.g. Canada Day Weekend Special" />
                <Box sx={{ display: "flex", gap: 1.5 }}>
                  <TextField label="Regular price" size="small" fullWidth disabled={busy}
                    value={regularPrice} onChange={(e) => setRegularPrice(e.target.value)}
                    placeholder="e.g. $14.99" />
                  <TextField label="Sale price" size="small" fullWidth disabled={busy}
                    value={salePrice} onChange={(e) => setSalePrice(e.target.value)}
                    placeholder="e.g. $9.99" />
                </Box>
                <Box sx={{ display: "flex", gap: 1.5 }}>
                  <TextField label="Promotion starts" type="date" size="small" fullWidth disabled={busy}
                    InputLabelProps={{ shrink: true }}
                    value={promoStart} onChange={(e) => setPromoStart(e.target.value)} />
                  <TextField label="Promotion ends" type="date" size="small" fullWidth disabled={busy}
                    InputLabelProps={{ shrink: true }}
                    value={promoEnd} onChange={(e) => setPromoEnd(e.target.value)} />
                </Box>
                <TextField label="Call to action" size="small" fullWidth disabled={busy}
                  value={cta} onChange={(e) => setCta(e.target.value)}
                  placeholder="e.g. Order now" />
                <TextField label="Disclaimer" size="small" fullWidth disabled={busy}
                  value={disclaimer} onChange={(e) => setDisclaimer(e.target.value)}
                  placeholder="e.g. While supplies last. Taxes extra." />
              </Stack>
            </Collapse>
          </Box>

          <Divider sx={{ borderColor: BORDER }} />

          {/* The in-flight indicator is now the blocking full-page overlay
              (rendered below), not a small inline spinner that's easy to miss. */}

          {result && !generating && (
            <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 1, p: 1.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: NAVY }}>
                  Saved to Media Library
                </Typography>
                <Chip size="small" color="warning" label="For review" />
              </Box>
              <Box
                component="img"
                src={result.url}
                alt={result.name}
                sx={{ width: "100%", borderRadius: 1, display: "block" }}
              />
              {resultMeta && (
                <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  <Chip size="small" variant="outlined" label={`Purpose: ${(resultMeta.purpose || "").replace("_", " ") || "—"}`} sx={{ height: 22, fontSize: 10.5, textTransform: "capitalize" }} />
                  <Chip size="small" variant="outlined" label={`Accuracy: ${ACCURACY_LABELS[resultMeta.accuracyMode as string] ?? "approximation"}`} sx={{ height: 22, fontSize: 10.5 }} />
                  <Chip size="small" variant="outlined" label={resultMeta.referenceUsed ? "Product reference used" : "No reference"} sx={{ height: 22, fontSize: 10.5 }} />
                  {resultMeta.productComposited && resultMeta.position && (
                    <Chip size="small" variant="outlined" label={`Position: ${resultMeta.position}`} sx={{ height: 22, fontSize: 10.5 }} />
                  )}
                  {resultMeta.productComposited && resultMeta.size && (
                    <Chip size="small" variant="outlined" label={`Size: ${resultMeta.size}`} sx={{ height: 22, fontSize: 10.5, textTransform: "capitalize" }} />
                  )}
                  {resultMeta.logoComposited && <Chip size="small" variant="outlined" label="Logo added" sx={{ height: 22, fontSize: 10.5 }} />}
                  {resultMeta.useTemplateZones && (
                    <Chip size="small" variant="outlined" label={`Layout: ${resultMeta.templateName || "template"}`} sx={{ height: 22, fontSize: 10.5 }} />
                  )}
                  {resultMeta.productZoneInherited && <Chip size="small" variant="outlined" label="Product zone inherited" sx={{ height: 22, fontSize: 10.5 }} />}
                  {resultMeta.logoZoneInherited && <Chip size="small" variant="outlined" label="Logo zone inherited" sx={{ height: 22, fontSize: 10.5 }} />}
                  <Chip size="small" color="success" variant="outlined" label="Saved to Media Library" sx={{ height: 22, fontSize: 10.5 }} />
                </Box>
              )}
              {resultMeta?.productComposited && (
                <Typography sx={{ fontSize: 11, color: NAVY, mt: 1, fontWeight: 600 }}>
                  Original product image composited without AI redrawing.
                </Typography>
              )}
              {resultMeta?.isApproximation && (
                <Typography sx={{ fontSize: 11, color: ERROR, mt: 1, fontWeight: 600 }}>
                  AI-generated approximation — product details may not be exact.
                </Typography>
              )}
              <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY, mt: 1 }}>
                This image is already saved to your Media Library as{" "}
                <strong>{result.name}</strong>. Regenerating creates a new,
                separate image in the library.
              </Typography>
            </Box>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, flexWrap: "wrap", gap: 1 }}>
        <Button onClick={handleClose} disabled={busy}>Close</Button>

        {result && (
          <Button onClick={reset} disabled={busy}>Generate Another</Button>
        )}
        {result && (
          <Button onClick={handleDownload} disabled={busy} startIcon={<DownloadIcon />}>
            Download
          </Button>
        )}

        {/* The asset is persisted by generation itself, so this is a truthful,
            non-clickable status — clicking must never imply a second save or
            create a duplicate record. */}
        {result && inPlaylist && (
          <Button disabled startIcon={<CheckCircleIcon />} sx={{ "&.Mui-disabled": { color: "success.main" } }}>
            Saved to Media Library
          </Button>
        )}

        <Button
          variant="contained" color="secondary"
          startIcon={generating ? <CircularProgress size={16} sx={{ color: NAVY }} /> : <AutoAwesomeIcon />}
          onClick={handleGenerate}
          disabled={!canGenerate}
        >
          {generating ? "Generating…" : result ? "Regenerate" : "Generate"}
        </Button>

        {result && inPlaylist && (
          <Button
            variant="contained" color="secondary"
            startIcon={isAdding ? <CircularProgress size={16} sx={{ color: NAVY }} /> : <PlaylistAddIcon />}
            onClick={handleAddToPlaylist}
            disabled={busy || added}
          >
            {added ? "Added" : "Save & Add to Playlist"}
          </Button>
        )}
      </DialogActions>
    </Dialog>

    {/* Blocking, full-viewport overlay — sits above the dialog, header, sidebar
        and snackbars so a multi-minute generation can never look frozen and
        nothing behind it can be clicked. Driven by the single authoritative
        flag. */}
    <FullPageLoadingOverlay
      open={isGeneratingImage}
      title="Generating your image"
      message="This may take up to a few minutes. Please keep this page open."
      statuses={[
        "Preparing your prompt…",
        "Sending request to AI…",
        "Generating image…",
        ...(includeLogo ? ["Adding your company logo…"] : []),
        "Saving to Media Library…",
      ]}
    />
    </>
  );
};

export default AiImageDialog;
